import React from 'react'
import styled from 'styled-components'

export type UserType = 'warehouse' | 'merchant'

// Role badge colors based on role hierarchy
const getWarehouseRoleBadgeColors = (role: string | null) => {
  if (!role) return { bg: '#f5f5f5', text: '#9e9e9e' }

  const roleLower = role.toLowerCase()
  // Admin-level roles (purple)
  if (roleLower.includes('executive') || roleLower.includes('owner')) {
    return { bg: '#f3e5f5', text: '#7b1fa2' }
  }
  // Support roles - Account Manager & Customer Service Rep (teal)
  if (roleLower.includes('account manager') || roleLower.includes('customer service')) {
    return { bg: '#e0f2f1', text: '#00695c' }
  }
  // Operator-level roles (green)
  if (roleLower.includes('manager') || roleLower.includes('operator') || roleLower.includes('lead')) {
    return { bg: '#e8f5e9', text: '#2e7d32' }
  }
  // Viewer-level roles (blue)
  if (roleLower.includes('viewer') || roleLower.includes('member') || roleLower.includes('employee')) {
    return { bg: '#e3f2fd', text: '#1565c0' }
  }
  // Associate role (light orange)
  if (roleLower.includes('associate')) {
    return { bg: '#fff3e0', text: '#e65100' }
  }
  // Default (blue for any other role)
  return { bg: '#e3f2fd', text: '#1565c0' }
}

const getMerchantRoleBadgeColors = (role: string | null) => {
  if (!role) return { bg: '#f5f5f5', text: '#9e9e9e' }

  const roleLower = role.toLowerCase()
  // Owner-level roles (purple)
  if (roleLower.includes('owner')) {
    return { bg: '#f3e5f5', text: '#7b1fa2' }
  }
  // Admin-level roles (green)
  if (roleLower.includes('admin')) {
    return { bg: '#e8f5e9', text: '#2e7d32' }
  }
  // Member-level roles (blue)
  if (roleLower.includes('member') || roleLower.includes('user')) {
    return { bg: '#e3f2fd', text: '#1565c0' }
  }
  // Default (blue for any other role)
  return { bg: '#e3f2fd', text: '#1565c0' }
}

export const getRoleBadgeColors = (role: string | null, userType: UserType = 'warehouse') => {
  return userType === 'warehouse' ? getWarehouseRoleBadgeColors(role) : getMerchantRoleBadgeColors(role)
}

interface RoleBadgeProps {
  role: string | null
  userType?: UserType
  children?: React.ReactNode
}

const StyledRoleBadge = styled.span<{ $bg: string; $text: string }>`
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.85em;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
`

export const RoleBadge = ({ role, userType = 'warehouse', children }: RoleBadgeProps) => {
  const colors = getRoleBadgeColors(role, userType)
  return (
    <StyledRoleBadge $bg={colors.bg} $text={colors.text}>
      {children || role || '—'}
    </StyledRoleBadge>
  )
}
